import type { CarnetEmpleado } from '@/types/dominio';
import { LOGO_GSH, LOGO_GSH_ANCHO, LOGO_GSH_ALTO } from './logoGsh';

/**
 * Carnet corporativo GSH en formato CR80 vertical (54 × 85,6 mm) a 300 ppp.
 *
 * Es un SVG puro, sin Tailwind ni variables CSS: se serializa fuera del
 * documento para exportarlo a PNG y para imprimirlo, así que todos los
 * estilos tienen que viajar dentro del propio SVG.
 */
export const ANCHO = 638;
export const ALTO = 1011;

const FONDO = '#F4F7FC';
const AZUL = '#1A56DB';
const AZUL_MEDIO = '#2563EB';
const NAVY = '#152C63';
const LAVANDA = '#DCE6F8';
const LAVANDA_SUAVE = '#E6ECF8';
const GRIS_LAVANDA = '#E3E9F6';

/**
 * Caja del logo, centrada y conservando la proporción del PNG original. El
 * ancho deja el isotipo por dentro de la mancha azul de la esquina superior
 * izquierda (que llega hasta x ≈ 78 a esta altura).
 */
const LOGO_ANCHO = 430;
const LOGO_ALTO = Math.round((LOGO_ANCHO * LOGO_GSH_ALTO) / LOGO_GSH_ANCHO);
const LOGO_Y = 112;

/** Caja del recuadro blanco que enmarca el QR. */
const QR_CAJA = { x: 119, y: 445, ancho: 400, alto: 400 };
const QR_LADO = 340;

/** "1020304050" → "1.020.304.050". Deja intactos los documentos no numéricos. */
export function formatearDocumento(documento: string): string {
  if (!/^\d+$/.test(documento)) return documento;
  return documento.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/**
 * Separa nombres de apellidos para maquetarlos en dos renglones con distinto
 * peso. Convención colombiana: la primera mitad son nombres de pila.
 */
export function partirNombre(nombre: string): { nombres: string; apellidos: string } {
  const palabras = nombre.trim().split(/\s+/).filter(Boolean);
  if (palabras.length === 0) return { nombres: '', apellidos: '' };
  if (palabras.length === 1) return { nombres: palabras[0], apellidos: '' };

  const corte = Math.ceil(palabras.length / 2);
  return {
    nombres: palabras.slice(0, corte).join(' '),
    apellidos: palabras.slice(corte).join(' '),
  };
}

/**
 * Cuerpo tipográfico que hace caber el texto en `maxAncho`. El SVG no ajusta
 * el texto solo; el avance medio en mayúsculas de una grotesca ronda 0,68 em.
 */
function cuerpoParaAncho(texto: string, maxAncho: number, maximo: number): number {
  if (!texto) return maximo;
  return Math.max(15, Math.min(maximo, Math.floor(maxAncho / (texto.length * 0.68))));
}

/** Retícula de puntos decorativa. */
function Puntos({ x, y, columnas, filas, paso, color, opacidad = 1 }: {
  x: number; y: number; columnas: number; filas: number; paso: number;
  color: string; opacidad?: number;
}) {
  const puntos = [];
  for (let f = 0; f < filas; f++) {
    for (let c = 0; c < columnas; c++) {
      puntos.push(
        <circle key={`${f}-${c}`} cx={x + c * paso} cy={y + f * paso} r="3" fill={color} opacity={opacidad} />,
      );
    }
  }
  return <>{puntos}</>;
}

/** Escuadra en una esquina del recuadro del QR. */
function Esquina({ x, y, dx, dy }: { x: number; y: number; dx: 1 | -1; dy: 1 | -1 }) {
  const brazo = 34;
  const radio = 12;
  return (
    <path
      d={`M${x + dx * brazo} ${y} L${x + dx * radio} ${y} Q${x} ${y} ${x} ${y + dy * radio} L${x} ${y + dy * brazo}`}
      fill="none" stroke={AZUL} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"
    />
  );
}

interface Props {
  carnet: CarnetEmpleado;
  svgRef?: React.Ref<SVGSVGElement>;
  /** Sufijo de los id internos: evita colisiones si se pintan varios carnets. */
  uid?: string;
  className?: string;
}

export function CarnetSvg({ carnet, svgRef, uid = 'c', className }: Props) {
  const { nombres, apellidos } = partirNombre(carnet.nombres);

  const cuerpoNombres = cuerpoParaAncho(nombres, 540, 46);
  const cuerpoApellidos = Math.min(
    cuerpoParaAncho(apellidos, 540, 36),
    Math.round(cuerpoNombres * 0.8),
  );
  const baseNombres = apellidos ? 352 : 375;
  const baseApellidos = baseNombres + Math.round(cuerpoNombres * 0.88);

  const textoDocumento = formatearDocumento(carnet.documento);
  // Ancho estimado de "C.C. " + número al cuerpo 23 (avance ≈ 0,58 em).
  const anchoTextoDoc = (textoDocumento.length + 5) * 23 * 0.58;
  const anchoPildoraDoc = Math.min(560, Math.round(anchoTextoDoc + 108));
  const xPildoraDoc = (ANCHO - anchoPildoraDoc) / 2;

  const recorte = `carnet-recorte-${uid}`;

  return (
    <svg
      ref={svgRef}
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      viewBox={`0 0 ${ANCHO} ${ALTO}`}
      width={ANCHO}
      height={ALTO}
      className={className}
      fontFamily="'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif"
    >
      <defs>
        <clipPath id={recorte}>
          <rect x="0" y="0" width={ANCHO} height={ALTO} rx="34" />
        </clipPath>
      </defs>

      <g clipPath={`url(#${recorte})`}>
        <rect x="0" y="0" width={ANCHO} height={ALTO} fill={FONDO} />

        {/* ── Formas de fondo ──────────────────────────────────────────── */}
        <path d="M0 262 C40 252 68 222 84 178 C104 122 124 60 158 0 H214 C186 68 174 132 152 190 C126 258 78 300 0 312 Z" fill={LAVANDA_SUAVE} />
        <path d="M0 0 H140 C132 58 100 84 78 122 C56 160 66 196 30 216 C20 222 10 224 0 226 Z" fill={AZUL} />
        <path d="M556 0 H638 V58 C598 52 568 30 556 0 Z" fill={GRIS_LAVANDA} />
        <path d="M638 92 C570 108 522 168 516 246 C510 330 556 396 616 442 C626 450 632 458 638 466 Z" fill={GRIS_LAVANDA} />
        <path d="M0 640 C36 656 62 690 74 734 C88 786 78 848 44 894 C30 913 14 926 0 936 Z" fill={GRIS_LAVANDA} />
        <path d="M0 676 C28 690 48 718 58 754" stroke={AZUL} strokeWidth="9" fill="none" strokeLinecap="round" />
        <path d="M638 700 C596 712 558 742 532 782 C510 816 502 874 506 936 H470 C464 870 478 806 508 762 C542 714 588 692 638 684 Z" fill={LAVANDA} />
        <path d="M638 800 C592 812 552 842 528 884 C512 912 508 926 508 936 H638 Z" fill={AZUL} />

        <Puntos x={540} y={38} columnas={4} filas={3} paso={19} color={AZUL} />
        <Puntos x={30} y={236} columnas={2} filas={3} paso={19} color={AZUL} />

        {/* ── Logo GSH ─────────────────────────────────────────────────────
            Marca oficial completa (isotipo + "gsh" + descriptor + tagline) en
            un solo PNG embebido: respeta la tipografía corporativa sin
            depender de una fuente instalada, que en el PNG exportado caería a
            la de reemplazo. */}
        <image
          href={LOGO_GSH}
          x={(ANCHO - LOGO_ANCHO) / 2}
          y={LOGO_Y}
          width={LOGO_ANCHO}
          height={LOGO_ALTO}
          preserveAspectRatio="xMidYMid meet"
        />
        {/* Franja azul que cierra el encabezado. Es el único separador aquí:
            el siguiente corte de la tarjeta es el de la línea con punto que
            va bajo el nombre. */}
        <rect x="180" y="266" width="278" height="8" rx="4" fill={AZUL} />

        {/* ── Identidad ────────────────────────────────────────────────── */}
        <text
          x={ANCHO / 2} y={baseNombres} textAnchor="middle"
          fontSize={cuerpoNombres} fontWeight="800" fill={NAVY} letterSpacing="0.5"
        >
          {nombres.toUpperCase()}
        </text>
        {apellidos && (
          <text
            x={ANCHO / 2} y={baseApellidos} textAnchor="middle"
            fontSize={cuerpoApellidos} fontWeight="700" fill={AZUL_MEDIO} letterSpacing="0.5"
          >
            {apellidos.toUpperCase()}
          </text>
        )}

        <line x1="98" y1="420" x2="303" y2="420" stroke={AZUL} strokeWidth="2" opacity="0.5" />
        <line x1="335" y1="420" x2="540" y2="420" stroke={AZUL} strokeWidth="2" opacity="0.5" />
        <circle cx={ANCHO / 2} cy="420" r="5" fill={AZUL} />

        {/* ── QR de acceso ─────────────────────────────────────────────── */}
        <rect
          x={QR_CAJA.x + 4} y={QR_CAJA.y + 7} width={QR_CAJA.ancho} height={QR_CAJA.alto}
          rx="18" fill={NAVY} opacity="0.07"
        />
        <rect
          x={QR_CAJA.x} y={QR_CAJA.y} width={QR_CAJA.ancho} height={QR_CAJA.alto}
          rx="18" fill="#FFFFFF"
        />
        <Esquina x={QR_CAJA.x + 18} y={QR_CAJA.y + 18} dx={1} dy={1} />
        <Esquina x={QR_CAJA.x + QR_CAJA.ancho - 18} y={QR_CAJA.y + 18} dx={-1} dy={1} />
        <Esquina x={QR_CAJA.x + 18} y={QR_CAJA.y + QR_CAJA.alto - 18} dx={1} dy={-1} />
        <Esquina x={QR_CAJA.x + QR_CAJA.ancho - 18} y={QR_CAJA.y + QR_CAJA.alto - 18} dx={-1} dy={-1} />
        <image
          href={carnet.qr_data_url}
          xlinkHref={carnet.qr_data_url}
          x={(ANCHO - QR_LADO) / 2}
          y={QR_CAJA.y + (QR_CAJA.alto - QR_LADO) / 2}
          width={QR_LADO} height={QR_LADO}
        />

        {/* ── Documento ────────────────────────────────────────────────── */}
        <rect x={xPildoraDoc} y="884" width={anchoPildoraDoc} height="44" rx="22" fill="#E4EBF8" />
        <g transform={`translate(${xPildoraDoc + 26} 896)`}>
          <rect x="0" y="0" width="26" height="20" rx="4" fill="none" stroke={AZUL} strokeWidth="2.2" />
          <circle cx="8" cy="8.5" r="3" fill={AZUL} />
          <path d="M3.5 15.5 C3.5 12.4 12.5 12.4 12.5 15.5" fill="none" stroke={AZUL} strokeWidth="2" strokeLinecap="round" />
          <line x1="16.5" y1="7" x2="22" y2="7" stroke={AZUL} strokeWidth="2" strokeLinecap="round" />
          <line x1="16.5" y1="11.5" x2="22" y2="11.5" stroke={AZUL} strokeWidth="2" strokeLinecap="round" />
        </g>
        <text x={xPildoraDoc + 68} y="913" fontSize="23" fontWeight="700" letterSpacing="0.5">
          <tspan fill={AZUL_MEDIO}>C.C. </tspan>
          <tspan fill={NAVY}>{textoDocumento}</tspan>
        </text>

        {/* ── Pie ──────────────────────────────────────────────────────── */}
        <rect x="0" y="936" width={ANCHO} height={ALTO - 936} fill={AZUL} />
        <g transform="translate(182 972)">
          <circle cx="0" cy="0" r="21" fill="none" stroke="#FFFFFF" strokeWidth="2.6" />
          <circle cx="0" cy="-6" r="4.6" fill="#FFFFFF" />
          <path d="M-8 6 C-8 -1 8 -1 8 6" fill="none" stroke="#FFFFFF" strokeWidth="3.4" strokeLinecap="round" />
          <circle cx="-10.5" cy="-2" r="3.4" fill="#FFFFFF" />
          <circle cx="10.5" cy="-2" r="3.4" fill="#FFFFFF" />
          <path d="M-15.5 8 C-15.5 3.5 -10.5 2.5 -7.5 4" fill="none" stroke="#FFFFFF" strokeWidth="2.8" strokeLinecap="round" />
          <path d="M15.5 8 C15.5 3.5 10.5 2.5 7.5 4" fill="none" stroke="#FFFFFF" strokeWidth="2.8" strokeLinecap="round" />
        </g>
        <text
          x="352" y="968" textAnchor="middle"
          fontSize="16.5" fontWeight="700" fill="#FFFFFF" letterSpacing="1.6"
        >
          PERSONAL E INTRANSFERIBLE
        </text>
        <line x1="250" y1="979" x2="454" y2="979" stroke="#FFFFFF" strokeWidth="1" opacity="0.45" />
        <text
          x="352" y="998" textAnchor="middle"
          fontSize="13.5" fontWeight="600" fill="#C6D9FA" letterSpacing="1.6"
        >
          PRESENTAR EN PORTERÍA
        </text>
      </g>
    </svg>
  );
}
